import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';

import { DevFlag, injectDevFlag } from '@recruitee/dev-flags-data';
import { OfferScope, StatusChangeType, TalentPoolsActions } from '@recruitee/offers-data';
import type { OfferData } from '@recruitee/offers-types';
import { OfferStatus } from '@recruitee/offers-types';
import type { TalentPool, TalentPoolData } from '@recruitee/talent-pools-types';

import { TalentPoolsNewActions } from './talent-pool/talent-pool.store';

type Offer = OfferData & { id: number };

@Injectable({ providedIn: 'root' })
export class TalentPoolsActionsService {
  private hasNewTalentPoolsFeature = injectDevFlag(DevFlag.NewTalentPools);
  private store = inject(Store);
  private actions$ = inject(Actions);

  public duplicate(id: number): void {
    if (this.hasNewTalentPoolsFeature) {
      this.store.dispatch(TalentPoolsNewActions.duplicate({ payload: { data: { id } } }));
    } else {
      this.store.dispatch(TalentPoolsActions.duplicate({ payload: { id } }));
    }
  }

  public getDuplicateSuccessAction(): Actions {
    if (this.hasNewTalentPoolsFeature) {
      return this.actions$.pipe(ofType(TalentPoolsNewActions.duplicateSuccess));
    } else {
      return this.actions$.pipe(ofType(TalentPoolsActions.duplicateSuccess));
    }
  }

  public getDuplicateFailedAction(): Actions {
    if (this.hasNewTalentPoolsFeature) {
      return this.actions$.pipe(ofType(TalentPoolsNewActions.duplicateFailed));
    } else {
      return this.actions$.pipe(ofType(TalentPoolsActions.duplicateFailed));
    }
  }

  public archive(id: number): void {
    if (this.hasNewTalentPoolsFeature) {
      this.store.dispatch(TalentPoolsNewActions.archive({ payload: { id } }));
    } else {
      this.store.dispatch(
        TalentPoolsActions.changeStatus({ payload: { id, status: StatusChangeType.Archive } }),
      );
    }
  }

  public getArchiveSuccessAction(): Actions {
    if (this.hasNewTalentPoolsFeature) {
      return this.actions$.pipe(ofType(TalentPoolsNewActions.archiveSuccess));
    } else {
      return this.actions$.pipe(
        ofType(TalentPoolsActions.changeStatusSuccess),
        filter(({ payload: { offer } }) => offer.status === OfferStatus.Archived),
      );
    }
  }

  public delete(id: number): void {
    if (this.hasNewTalentPoolsFeature) {
      this.store.dispatch(TalentPoolsNewActions.removeOnePessimistic({ payload: { data: id } }));
    } else {
      this.store.dispatch(TalentPoolsActions.removeOne({ payload: { id } }));
    }
  }

  public getDeleteSuccessAction(): Actions {
    if (this.hasNewTalentPoolsFeature) {
      return this.actions$.pipe(ofType(TalentPoolsNewActions.removeOnePessimisticSuccess));
    } else {
      return this.actions$.pipe(ofType(TalentPoolsActions.removeOneSuccess));
    }
  }

  public getDeleteFailedAction(): Actions {
    if (this.hasNewTalentPoolsFeature) {
      return this.actions$.pipe(ofType(TalentPoolsNewActions.removeOnePessimisticFailed));
    } else {
      return this.actions$.pipe(ofType(TalentPoolsActions.removeOneFailed));
    }
  }

  public retrieve(id: number): void {
    if (this.hasNewTalentPoolsFeature) {
      this.store.dispatch(TalentPoolsNewActions.retrieve({ payload: { id } }));
    } else {
      this.store.dispatch(
        TalentPoolsActions.changeStatus({ payload: { id, status: StatusChangeType.Draft } }),
      );
    }
  }

  public getRetrieveSuccessAction(): Actions {
    if (this.hasNewTalentPoolsFeature) {
      return this.actions$.pipe(ofType(TalentPoolsNewActions.retrieveSuccess));
    } else {
      return this.actions$.pipe(
        ofType(TalentPoolsActions.changeStatusSuccess),
        filter(({ payload: { offer } }) => offer.status === OfferStatus.Draft),
      );
    }
  }

  public follow(id: number): void {
    if (this.hasNewTalentPoolsFeature) {
      this.store.dispatch(TalentPoolsNewActions.follow({ payload: { id } }));
    } else {
      this.store.dispatch(TalentPoolsActions.follow({ payload: { id, scope: OfferScope.Active } }));
    }
  }

  public getFollowSuccessAction(): Actions {
    if (this.hasNewTalentPoolsFeature) {
      return this.actions$.pipe(ofType(TalentPoolsNewActions.followSuccess));
    } else {
      return this.actions$.pipe(ofType(TalentPoolsActions.followSuccess));
    }
  }

  public unfollow(id: number): void {
    if (this.hasNewTalentPoolsFeature) {
      this.store.dispatch(TalentPoolsNewActions.unfollow({ payload: { id } }));
    } else {
      this.store.dispatch(
        TalentPoolsActions.unfollow({ payload: { id, scope: OfferScope.Active } }),
      );
    }
  }

  public getUnfollowSuccessAction(): Actions {
    if (this.hasNewTalentPoolsFeature) {
      return this.actions$.pipe(ofType(TalentPoolsNewActions.unfollowSuccess));
    } else {
      return this.actions$.pipe(ofType(TalentPoolsActions.unfollowSuccess));
    }
  }

  public create(talentPool: TalentPoolData): void {
    if (this.hasNewTalentPoolsFeature) {
      this.store.dispatch(
        TalentPoolsNewActions.createOnePessimistic({ payload: { data: talentPool } }),
      );
    } else {
      this.store.dispatch(TalentPoolsActions.create({ payload: { offer: <Offer>talentPool } }));
    }
  }

  public getCreateSuccessAction(): Actions<{ payload: { data: TalentPool } }> {
    if (this.hasNewTalentPoolsFeature) {
      return this.actions$.pipe(ofType(TalentPoolsNewActions.createOnePessimisticSuccess));
    } else {
      return this.actions$.pipe(ofType(TalentPoolsActions.createSuccess));
    }
  }

  public getCreateFailedAction(): Actions {
    if (this.hasNewTalentPoolsFeature) {
      return this.actions$.pipe(ofType(TalentPoolsNewActions.createOnePessimisticFailed));
    } else {
      return this.actions$.pipe(ofType(TalentPoolsActions.createFailed));
    }
  }

  public update(talentPool: TalentPoolData & { id: number }): void {
    if (this.hasNewTalentPoolsFeature) {
      this.store.dispatch(
        TalentPoolsNewActions.updateOnePessimistic({ payload: { data: talentPool } }),
      );
    } else {
      this.store.dispatch(TalentPoolsActions.update({ payload: { offer: <Offer>talentPool } }));
    }
  }

  public getUpdateSuccessAction(): Actions<{ payload: { data: TalentPool } }> {
    if (this.hasNewTalentPoolsFeature) {
      return this.actions$.pipe(ofType(TalentPoolsNewActions.updateOnePessimisticSuccess));
    } else {
      return this.actions$.pipe(ofType(TalentPoolsActions.updateSuccess));
    }
  }

  public getUpdateFailedAction(): Actions {
    if (this.hasNewTalentPoolsFeature) {
      return this.actions$.pipe(ofType(TalentPoolsNewActions.updateOnePessimisticFailed));
    } else {
      return this.actions$.pipe(ofType(TalentPoolsActions.updateFailed));
    }
  }

  public updatePositions(
    positions: Array<{
      id: number;
      position: number;
    }>,
  ): void {
    if (this.hasNewTalentPoolsFeature) {
      this.store.dispatch(TalentPoolsNewActions.updatePositions({ payload: { positions } }));
    } else {
      this.store.dispatch(TalentPoolsActions.updatePositions({ payload: positions }));
    }
  }
}
